import { useCallback, useEffect, useState } from 'react';
import adminApi from '@/lib/adminApi';

const inr = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

const errorMessage = (error) => {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' ? detail : 'Could not load Learn cost data.';
};

export default function AdminLearnCosts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [appliedRange, setAppliedRange] = useState({ from: '', to: '' });
  const [page, setPage] = useState(1);

  const loadCosts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await adminApi.get('/admin/learn/costs', {
        params: {
          ...(appliedRange.from ? { date_from: appliedRange.from } : {}),
          ...(appliedRange.to ? { date_to: appliedRange.to } : {}),
          page,
          page_size: 20,
        },
      });
      setData(response.data);
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [appliedRange, page]);

  useEffect(() => {
    loadCosts();
  }, [loadCosts]);

  const applyDateRange = (event) => {
    event.preventDefault();
    setPage(1);
    setAppliedRange({ from: dateFrom, to: dateTo });
  };

  const clearDateRange = () => {
    setDateFrom('');
    setDateTo('');
    setPage(1);
    setAppliedRange({ from: '', to: '' });
  };

  const summary = data?.summary || {};
  const pagination = data?.pagination || { page: 1, total_pages: 1, total_items: 0 };
  const metrics = [
    { label: 'Total submissions', value: Number(summary.total_submissions || 0).toLocaleString('en-IN') },
    { label: 'Total cost', value: inr.format(summary.total_cost_inr || 0) },
    { label: 'Average cost / submission', value: inr.format(summary.average_cost_per_submission_inr || 0), featured: true },
    { label: 'Cost / active learner', value: inr.format(summary.cost_per_active_learner_inr || 0), note: `${summary.active_learners || 0} active learner(s)` },
  ];

  return (
    <div className="space-y-7">
      <div>
        <p className="text-xs font-black uppercase tracking-[0.22em] text-[#FF6F00]">Learn</p>
        <h2 className="mt-2 text-3xl font-black text-gray-950">Cost dashboard</h2>
        <p className="mt-2 max-w-3xl text-sm font-medium text-gray-600">
          All recorded Learn generation, feedback, transcription and storage costs.
        </p>
      </div>

      {data?.accounting && <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
        <p className="font-bold">Learn {data.accounting.learn_enabled ? 'enabled' : 'paused'} · {data.accounting.usage_record_count} usage records · {data.accounting.unpriced_record_count} records awaiting pricing or usage</p>
        <p className="mt-2">{data.accounting.note}</p>
      </div>}

      <form onSubmit={applyDateRange} className="flex flex-wrap items-end gap-3 rounded-3xl border border-orange-100 bg-white p-5 shadow-sm">
        <label className="grid gap-1.5 text-sm font-bold text-gray-700">
          From
          <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} className="rounded-xl border border-gray-200 px-3 py-2 font-medium" />
        </label>
        <label className="grid gap-1.5 text-sm font-bold text-gray-700">
          To
          <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} className="rounded-xl border border-gray-200 px-3 py-2 font-medium" />
        </label>
        <button type="submit" className="rounded-xl bg-[#FF6F00] px-5 py-2.5 text-sm font-black text-white">Apply</button>
        <button type="button" onClick={clearDateRange} className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-black text-gray-700">Clear</button>
      </form>

      {error && <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">{error}</div>}

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-label="Cost summary">
        {metrics.map((metric) => (
          <article key={metric.label} className={`rounded-3xl border p-5 shadow-sm ${metric.featured ? 'border-purple-200 bg-purple-50' : 'border-orange-100 bg-white'}`}>
            <p className="text-xs font-black uppercase tracking-[0.14em] text-gray-500">{metric.label}</p>
            <p className="mt-3 text-2xl font-black text-gray-950">{loading && !data ? '…' : metric.value}</p>
            {metric.note && <p className="mt-2 text-xs font-bold text-gray-500">{metric.note}</p>}
          </article>
        ))}
      </section>

      <section className="rounded-3xl border border-orange-100 bg-white p-5 shadow-sm">
        <h3 className="text-xl font-black text-gray-950">Cost by provider</h3>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {(data?.providers || []).map((provider) => (
            <article key={provider.key} className="rounded-2xl bg-gray-50 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-black text-gray-900">{provider.display_name}</p>
                  <p className="text-xs font-bold text-gray-500">{provider.purpose}</p>
                </div>
                <p className="font-black text-gray-950">{inr.format(provider.cost_inr || 0)}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="overflow-hidden rounded-3xl border border-amber-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-5 py-4">
          <h3 className="text-xl font-black text-gray-950">AI feedback demand</h3>
          <p className="mt-1 text-xs font-bold text-gray-500">All-time feature-notification interest, ranked highest first · {data?.interest_demand?.total_signals || 0} signal(s)</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-amber-50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-5 py-3">Art form</th>
                <th className="px-5 py-3">Curriculum language</th>
                <th className="px-5 py-3 text-right">Interested learners</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data?.interest_demand?.rows || []).map((row) => (
                <tr key={`${row.art_form_id}-${row.language}`} className="text-gray-700">
                  <td className="px-5 py-4 font-black text-gray-950">{row.art_form_name}</td>
                  <td className="px-5 py-4">{row.language_label}</td>
                  <td className="px-5 py-4 text-right text-lg font-black text-[#FF6F00]">{row.interest_count}</td>
                </tr>
              ))}
              {!loading && (data?.interest_demand?.rows || []).length === 0 && (
                <tr><td colSpan="3" className="px-5 py-8 text-center font-bold text-gray-500">No coming-soon interest captured yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="overflow-hidden rounded-3xl border border-orange-100 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div>
            <h3 className="text-xl font-black text-gray-950">Recent submissions</h3>
            <p className="mt-1 text-xs font-bold text-gray-500">Most recent first · {pagination.total_items || 0} submission(s)</p>
          </div>
          {data?.exchange_rate_usd_to_inr != null && (
            <p className="text-xs font-bold text-gray-500">USD costs shown at ₹{data.exchange_rate_usd_to_inr}/USD</p>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-5 py-3">Date</th>
                <th className="px-5 py-3">Learner email</th>
                <th className="px-5 py-3">Art form</th>
                <th className="px-5 py-3">Cost (INR)</th>
                <th className="px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data?.rows || []).map((row) => (
                <tr key={row.submission_id} className="text-gray-700">
                  <td className="whitespace-nowrap px-5 py-4 font-medium">{formatDate(row.date)}</td>
                  <td className="px-5 py-4 font-bold text-gray-900">{row.learner_email}</td>
                  <td className="px-5 py-4">{row.art_form}</td>
                  <td className="whitespace-nowrap px-5 py-4 font-black">{inr.format(row.cost_inr || 0)}</td>
                  <td className="px-5 py-4"><span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-black capitalize">{String(row.status || '').replaceAll('_', ' ')}</span></td>
                </tr>
              ))}
              {!loading && (data?.rows || []).length === 0 && (
                <tr><td colSpan="5" className="px-5 py-10 text-center font-bold text-gray-500">No submissions in this date range.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between gap-4 border-t border-gray-100 px-5 py-4">
          <button type="button" disabled={loading || page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))} className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-black text-gray-700 disabled:cursor-not-allowed disabled:opacity-40">Previous</button>
          <p className="text-sm font-bold text-gray-600">Page {pagination.page || 1} of {pagination.total_pages || 1}</p>
          <button type="button" disabled={loading || page >= (pagination.total_pages || 1)} onClick={() => setPage((current) => current + 1)} className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-black text-gray-700 disabled:cursor-not-allowed disabled:opacity-40">Next</button>
        </div>
      </section>

      <p className="text-xs font-medium text-gray-500">Active learner means an account with at least one submission in the selected date range.</p>
    </div>
  );
}
